import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  type EdgeChange,
  MiniMap,
  type NodeChange,
  ReactFlow,
  ReactFlowInstance,
} from '@xyflow/react';
import styled, { useTheme } from 'styled-components';

import { ViewModeContext, ViewMode } from 'state/ViewModeContext';
import { StepRunContext, type StepRunColors } from 'state/StepRunContext';
import type {
  ControlEdgeData,
  WorkflowEdge,
  WorkflowGraph,
  WorkflowNode as WorkflowNodeT,
} from 'model/xyflowMapping';
import { useCanvasConnections } from 'hooks/useCanvasConnections';
import { parseJson } from 'utils/parseJson';
import { DRAG_DATA_TYPE } from 'hooks/useDragDrop';
import { createNodesFromDrop } from 'utils/workflowGraphOps';
import { WorkflowNode } from './WorkflowNode';

import '@xyflow/react/dist/style.css';

const StyledCanvasContainer = styled.div`
  width: 100%;
  height: 100%;
`;

export interface WorkflowCanvasHandle {
  fitView: () => void;
  screenToFlowPosition: (pos: { x: number; y: number }) => { x: number; y: number };
}

type GraphUpdater = WorkflowGraph | ((prev: WorkflowGraph) => WorkflowGraph);

interface OwnProps {
  graph: WorkflowGraph;
  onGraphChange: (next: GraphUpdater) => void;
  onNodeSelected: (nodeId: string | null) => void;
  onSelectionChange?: (nodeIds: string[]) => void;
  onNodesDeleted?: (nodeIds: string[]) => void;
  onNodeContextMenu?: (x: number, y: number, nodeId: string) => void;
  onEdgeContextMenu?: (x: number, y: number, edgeId: string) => void;
  onEdgeDoubleClick?: (edgeId: string) => void;
  onPaneContextMenu?: (x: number, y: number) => void;
  onNodeDragStart?: () => void;
  onNodeDragStop?: () => void;
  onConnectInputBinding?: (
    targetId: string,
    fieldName: string,
    sourceId: string,
    outputName: string,
  ) => void;
  viewMode: ViewMode;
  edgeType?: string;
  stepRunColors?: StepRunColors;
  badEdgeIds?: Set<string>;
}

const nodeTypes = { workflowNode: WorkflowNode };
const deleteKeyCode = ['Delete', 'Backspace'];

const WorkflowCanvasInner = forwardRef<WorkflowCanvasHandle, OwnProps>(
  (
    {
      graph,
      onGraphChange,
      onNodeSelected,
      onSelectionChange,
      onNodesDeleted,
      onNodeContextMenu,
      onEdgeContextMenu,
      onEdgeDoubleClick,
      onPaneContextMenu,
      onNodeDragStart,
      onNodeDragStop,
      onConnectInputBinding,
      viewMode,
      edgeType = 'default',
      stepRunColors = null,
      badEdgeIds,
    }: OwnProps,
    ref,
  ) => {
    const theme = useTheme();
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const reactFlowInstanceRef = useRef<ReactFlowInstance<WorkflowNodeT, WorkflowEdge> | null>(
      null,
    );

    useImperativeHandle(ref, () => ({
      fitView: () => {
        reactFlowInstanceRef.current?.fitView({ padding: 0.1 });
      },
      screenToFlowPosition: (pos: { x: number; y: number }) =>
        reactFlowInstanceRef.current?.screenToFlowPosition(pos) ?? pos,
    }));

    const handleNodesChange = useCallback(
      (changes: NodeChange<WorkflowNodeT>[]) => {
        onGraphChange((prev) => ({
          nodes: applyNodeChanges(changes, prev.nodes),
          edges: prev.edges,
        }));
      },
      [onGraphChange],
    );

    const handleEdgesChange = useCallback(
      (changes: EdgeChange<WorkflowEdge>[]) => {
        onGraphChange((prev) => ({
          nodes: prev.nodes,
          edges: applyEdgeChanges(changes, prev.edges),
        }));
      },
      [onGraphChange],
    );

    // Shim that mirrors React's `useState` setter shape so
    // `useCanvasConnections` can stay an isolated "given setEdges, produce
    // connection handlers" hook without learning about the parent graph.
    const setEdgesProxy = useCallback<React.Dispatch<React.SetStateAction<WorkflowEdge[]>>>(
      (updater) => {
        onGraphChange((prev) => ({
          nodes: prev.nodes,
          edges: typeof updater === 'function' ? updater(prev.edges) : updater,
        }));
      },
      [onGraphChange],
    );

    const { handleConnect, isValidConnection } = useCanvasConnections({
      setEdges: setEdgesProxy,
      onConnectInputBinding,
    });

    const handleInit = useCallback((instance: ReactFlowInstance<WorkflowNodeT, WorkflowEdge>) => {
      reactFlowInstanceRef.current = instance;
    }, []);

    const handleNodeClick = useCallback<
      NonNullable<React.ComponentProps<typeof ReactFlow>['onNodeClick']>
    >(
      (_event, node) => {
        onNodeSelected(node.id);
      },
      [onNodeSelected],
    );

    const handlePaneClick = useCallback<
      NonNullable<React.ComponentProps<typeof ReactFlow>['onPaneClick']>
    >(() => {
      onNodeSelected(null);
    }, [onNodeSelected]);

    const handleNodesDelete = useCallback(
      (deleted: WorkflowNodeT[]) => {
        const ids = deleted.map((n) => n.id);
        onNodesDeleted?.(ids);
      },
      [onNodesDeleted],
    );

    const handleEdgesDelete = useCallback(() => {
      // Edge removal is already applied through onEdgesChange → onGraphChange;
      // no further work is needed here.
    }, []);

    const handleNodeContextMenu = useCallback(
      (event: React.MouseEvent, node: WorkflowNodeT) => {
        event.preventDefault();
        onNodeContextMenu?.(event.clientX, event.clientY, node.id);
      },
      [onNodeContextMenu],
    );

    const handlePaneContextMenu = useCallback(
      (event: React.MouseEvent | MouseEvent) => {
        event.preventDefault();
        onPaneContextMenu?.(event.clientX, event.clientY);
      },
      [onPaneContextMenu],
    );

    const handleEdgeContextMenu = useCallback(
      (event: React.MouseEvent, edge: WorkflowEdge) => {
        event.preventDefault();
        onEdgeContextMenu?.(event.clientX, event.clientY, edge.id);
      },
      [onEdgeContextMenu],
    );

    const handleEdgeDoubleClickInternal = useCallback(
      (_event: React.MouseEvent, edge: WorkflowEdge) => {
        onEdgeDoubleClick?.(edge.id);
      },
      [onEdgeDoubleClick],
    );

    const handleNodeDragStart = useCallback(() => {
      onNodeDragStart?.();
    }, [onNodeDragStart]);

    const handleNodeDragStop = useCallback(() => {
      onNodeDragStop?.();
    }, [onNodeDragStop]);

    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.dataTransfer.dropEffect = 'move';
    }, []);

    const handleDrop = useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();

        const instance = reactFlowInstanceRef.current;

        if (!instance) {
          return;
        }

        const raw = event.dataTransfer.getData(DRAG_DATA_TYPE);
        if (!raw) {
          return;
        }

        const parsed = parseJson<{ actionId: string }>(raw);
        if (!parsed.ok || parsed.value === null) {
          return;
        }
        const payload = parsed.value;

        const position = instance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        onGraphChange((prev) => {
          const created = createNodesFromDrop(payload.actionId, position, prev.nodes);
          if (!created) return prev;
          return { nodes: prev.nodes.concat(created), edges: prev.edges };
        });
      },
      [onGraphChange],
    );

    const handleSelectionChange = useCallback(
      (params: { nodes?: WorkflowNodeT[] } | null) => {
        const ids = (params?.nodes ?? []).map((n) => n.id);
        onSelectionChange?.(ids);
      },
      [onSelectionChange],
    );

    const handleNodeDoubleClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    const visibleEdges = useMemo<WorkflowEdge[]>(() => {
      const filtered =
        viewMode === 'flow'
          ? graph.edges.filter((e) => (e.data as ControlEdgeData).kind === 'control')
          : graph.edges;
      return filtered.map((e) => {
        const data = e.data as ControlEdgeData;
        const isBad = badEdgeIds?.has(e.id) ?? false;
        const badStyle: React.CSSProperties = isBad
          ? { stroke: theme.colors.dangerText, strokeWidth: 2 }
          : {};
        // Re-derive style so that toggling enableOnCondition in the side panel is reflected immediately
        if (data?.kind === 'control' && !data?.routeKey) {
          const isConditional = data.enableOnCondition === true;
          return {
            ...e,
            type: edgeType,
            style: isConditional
              ? { ...(e.style ?? {}), strokeDasharray: '5 5', strokeWidth: 1.5, ...badStyle }
              : { ...(e.style ?? {}), strokeDasharray: undefined, strokeWidth: 1.5, ...badStyle },
          };
        }

        return { ...e, type: edgeType, style: { ...(e.style ?? {}), ...badStyle } };
      });
    }, [graph.edges, viewMode, edgeType, badEdgeIds, theme.colors.dangerText]);

    return (
      <StepRunContext.Provider value={stepRunColors}>
        <ViewModeContext.Provider value={viewMode}>
          <StyledCanvasContainer ref={wrapperRef} onDrop={handleDrop} onDragOver={handleDragOver}>
            <ReactFlow
              nodes={graph.nodes}
              edges={visibleEdges}
              nodeTypes={nodeTypes}
              onNodesChange={handleNodesChange}
              onEdgesChange={handleEdgesChange}
              onConnect={handleConnect}
              onNodeClick={handleNodeClick}
              onPaneClick={handlePaneClick}
              onNodesDelete={handleNodesDelete}
              onEdgesDelete={handleEdgesDelete}
              onNodeContextMenu={handleNodeContextMenu}
              onEdgeContextMenu={handleEdgeContextMenu}
              onEdgeDoubleClick={handleEdgeDoubleClickInternal}
              onPaneContextMenu={handlePaneContextMenu}
              onNodeDragStart={handleNodeDragStart}
              onNodeDragStop={handleNodeDragStop}
              onSelectionChange={handleSelectionChange}
              onNodeDoubleClick={handleNodeDoubleClick}
              zoomOnDoubleClick={false}
              deleteKeyCode={deleteKeyCode}
              onInit={handleInit}
              isValidConnection={isValidConnection}
              fitView
            >
              <Background />
              <MiniMap />
              <Controls />
            </ReactFlow>
          </StyledCanvasContainer>
        </ViewModeContext.Provider>
      </StepRunContext.Provider>
    );
  },
);

WorkflowCanvasInner.displayName = 'WorkflowCanvas';

const MemoWorkflowCanvas = React.memo(WorkflowCanvasInner);

export { MemoWorkflowCanvas as WorkflowCanvas };
export default MemoWorkflowCanvas;
